import React from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { BannerEvent } from '../../../api/bannerOffers/types';
import ProjectCalendarPicker from '../../../components/dateAndTime/ProjectCalendarPicker';
import { addDaysToLocalString, dateToLocalString, localStringToDate, MAX_CTA_LENGTH, MAX_LABEL_LENGTH } from '../utils';

const DURATION_PRESETS = [
  { label: '1 day', days: 1 },
  { label: '3 days', days: 3 },
  { label: '1 week', days: 7 },
  { label: '2 weeks', days: 14 },
];

const EMPTY_EVENT: BannerEvent = { start: '', end: '', emoji: '', label: '', cta: '', occasion: '' };

export const eventSchema = yup.object({
  start: yup.string().required('Start date is required'),
  end: yup
    .string()
    .required('End date is required')
    .test('end-after-start', 'End must be on or after start', function (value) {
      const { start } = this.parent;
      return !start || !value || value >= start;
    }),
  emoji: yup.string().trim().required('Emoji is required'),
  label: yup.string().trim().required('Label is required').max(MAX_LABEL_LENGTH, `Max ${MAX_LABEL_LENGTH} characters`),
  cta: yup.string().trim().required('CTA is required').max(MAX_CTA_LENGTH, `Max ${MAX_CTA_LENGTH} characters`),
  occasion: yup.string(),
});

const EventModal = ({
  event,
  otherEvents,
  isEdit = Boolean(event),
  onClose,
  onSave,
}: {
  /** undefined = closed, null = create, object = edit */
  event: BannerEvent | null | undefined;
  /** All events except the one being edited, used for the overlap hint. */
  otherEvents: BannerEvent[];
  /** false for create and duplicate, even when `event` is a prefilled draft */
  isEdit?: boolean;
  onClose: () => void;
  onSave: (event: BannerEvent) => void;
}) => {
  const formik = useFormik<BannerEvent>({
    initialValues: event || EMPTY_EVENT,
    enableReinitialize: true,
    validationSchema: eventSchema,
    onSubmit: (values) =>
      onSave({
        ...values,
        emoji: values.emoji.trim(),
        label: values.label.trim(),
        cta: values.cta.trim(),
        occasion: (values.occasion || '').trim(),
      }),
  });
  const { values, errors, touched, handleChange, setFieldValue, setFieldTouched } = formik;

  const overlaps = otherEvents.filter(
    (other) => values.start && values.end && other.start <= values.end && values.start <= other.end,
  );

  const onDateChange = (name: 'start' | 'end', date: Date | null) => {
    const value = date ? dateToLocalString(date) : '';
    setFieldValue(name, value);
    setFieldTouched(name, true, false);
    // Keep the range valid: a new start fills or pushes the end to the same day.
    if (name === 'start' && value && (!values.end || values.end < value)) {
      setFieldValue('end', value);
    }
  };

  const dateField = (name: 'start' | 'end', label: string) => (
    <Box flex={1}>
      <Typography variant='caption'>{label}</Typography>
      <ProjectCalendarPicker
        variant='white'
        placeholder={`Set ${name} date`}
        dateValue={localStringToDate(values[name])}
        onDateValueChange={(date) => onDateChange(name, date)}
      />
      {errors[name] && touched[name] ? (
        <Typography variant='caption' color='error' role='alert'>
          {errors[name]}
        </Typography>
      ) : null}
    </Box>
  );

  const textField = (name: 'emoji' | 'label' | 'cta' | 'occasion', label: string, helper?: string) => (
    <TextField
      size='small'
      fullWidth
      name={name}
      label={label}
      value={values[name]}
      onChange={handleChange}
      onBlur={formik.handleBlur}
      error={Boolean(errors[name] && touched[name])}
      helperText={errors[name] && touched[name] ? errors[name] : helper}
    />
  );

  return (
    <Dialog open={event !== undefined} onClose={onClose} fullWidth maxWidth='sm'>
      <form onSubmit={formik.handleSubmit} noValidate>
        <DialogTitle>{isEdit ? 'Edit event' : 'New event'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Box>
              <Typography variant='subtitle2' mb={1}>
                When
              </Typography>
              <Stack direction='row' spacing={2}>
                {dateField('start', 'Start')}
                {dateField('end', 'End (inclusive)')}
              </Stack>
              <Stack direction='row' spacing={1} mt={1} alignItems='center' flexWrap='wrap'>
                <Typography variant='caption' color='text.secondary'>
                  Quick length:
                </Typography>
                {DURATION_PRESETS.map((preset) => (
                  <Chip
                    key={preset.days}
                    size='small'
                    variant='outlined'
                    label={preset.label}
                    disabled={!values.start}
                    onClick={() => setFieldValue('end', addDaysToLocalString(values.start, preset.days - 1))}
                  />
                ))}
              </Stack>
              {overlaps.length > 0 ? (
                <Alert severity='warning' sx={{ mt: 1.5 }}>
                  Overlaps {overlaps.map((o) => o.label).join(', ')}. On shared days the shortest event wins.
                </Alert>
              ) : null}
            </Box>

            <Box>
              <Typography variant='subtitle2' mb={1}>
                Banner
              </Typography>
              <Stack spacing={2}>
                {textField('emoji', 'Emoji')}
                {textField('label', 'Label', `${values.label.length}/${MAX_LABEL_LENGTH}`)}
                {textField('cta', 'Button text', `${values.cta.length}/${MAX_CTA_LENGTH}`)}
                {textField('occasion', 'Occasion (internal note, optional)')}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type='submit' variant='contained'>
            {isEdit ? 'Update event' : 'Add event'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EventModal;
